import React, { useState } from 'react';
import Modal from '../components/Modal';
import IdentityCard from '../components/IdentityCard';

const Writings: React.FC = () => {
  const [selectedArticle, setSelectedArticle] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleArticleClick = (articleId: string) => {
    setSelectedArticle(articleId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedArticle(null);
  };

  const articles = [
    {
      id: 'research-methods',
      title: 'How to Select the Right Research Method for Your UX Project',
      tags: ['Research Methods', 'UX Strategy'],
      description: [
        'Every UX research project begins with a question. And behind that question lies another, often more difficult one — how should I go about answering it?',
        'Selecting the right research method isn\'t just a matter of logistics, it\'s a matter of epistemology. It reflects what we believe is knowable, how we define validity, and what kinds of insights we value.'
      ],
      content: `
        <div class="project-modal-content">
          <section class="modal-section">
            <h3 class="modal-section-title">Research Context</h3>
            <div class="modal-content-block">
              <p class="modal-text">In applied UX research, these decisions are often shaped by constraints: time, stakeholder expectations, available users. But that doesn't mean we abandon rigor. Instead, we adapt with intentionality.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Methods Reflect the Kind of Answers You Want</h3>
            <p class="modal-intro-text">Let's start with the basics. If your question is exploratory; "What are users trying to do here?" or "Why do they drop off at this step?"; you likely need qualitative methods: user interviews, contextual inquiry, ethnographic field notes, think-aloud protocols. These methods give you rich narratives and uncover what metrics alone won't show.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Qualitative vs Quantitative Approaches</h4>
              <p class="modal-text">On the other hand, if your question is evaluative; "Did this redesign reduce task time?" or "How satisfied are users post-launch?"; you might need quantitative methods: surveys, A/B testing, heatmaps, clickstream analysis. These give you scale, structure, and statistical confidence.</p>
              <p class="modal-text">Most projects benefit from a bit of both. But the danger lies in defaulting to one because it's convenient. Just because you <em>can</em> run a survey doesn't mean you <em>should</em>. The method must match the question, not just the budget.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">From Hypothesis to Approach</h3>
            <p class="modal-intro-text">One of the most helpful exercises I've developed for myself is to write the research question in plain language, followed by a hypothesis (if applicable), then ask: what kind of evidence would challenge or support this?</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Example Framework</h4>
              <ul class="insight-list">
                <li><strong>Question:</strong> Why are users abandoning the profile setup page?</li>
                <li><strong>Hypothesis:</strong> The form is too long and confusing.</li>
                <li><strong>Needed evidence:</strong> Observational data, user narratives, possibly usability test metrics.</li>
              </ul>
            </div>
            
            <p class="modal-text">This framing prevents method drift. It reminds me that the purpose isn't to run a test, it's to learn something real about users.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Constraints Are Not Excuses</h3>
            <p class="modal-intro-text">It's easy to say "we only had a week" or "we only had five users" as if these limitations invalidate the work. They don't. But they <em>do</em> shape what claims we can responsibly make.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Real-World Example</h4>
              <p class="modal-text">When I worked on a constrained mobile testing project, we had just three days and no lab access. We set up remote task tests and followed them with brief interviews. The findings were narrow but actionable. I made sure to document the limitations and frame the results as directional, not definitive.</p>
            </div>
            
            <p class="modal-text">This is key: method selection doesn't just involve choosing <em>what</em> to do. It involves being transparent about what that choice allows you to claim and what it doesn't.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Don't Confuse Tools with Methods</h3>
            <p class="modal-intro-text">I've seen teams confuse usability testing software or survey platforms with the methods themselves. Tools are enablers, not frameworks. Knowing how to use Optimal Workshop doesn't mean you've chosen the right kind of card sort. Running a test in Maze doesn't mean you've conducted a rigorous usability evaluation.</p>
            
            <p class="modal-text">Choosing a method means thinking about data types, user context, ethical implications, and analytical paths. It's a design decision, not a plug-and-play action.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">A Word on Hybrid Designs</h3>
            <p class="modal-intro-text">Sometimes, the best research design blends methods. I've combined intercept surveys with follow-up interviews. I've run diary studies that culminated in live usability walkthroughs. These combinations allow me to see behavior from multiple angles: what people say, what they do, and how they interpret it.</p>
            
            <p class="modal-text">But hybrid designs must be integrated with care. Mixing methods isn't about doing more for the sake of volume. It's about cross-validating insights and giving each method space to breathe.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Thinking Through Cognitive Load</h3>
            <p class="modal-intro-text">One thing I have understood to be paramount, is awareness of participant burden. Every research method imposes a cognitive load. A five-step usability test with a think-aloud protocol can be mentally exhausting. A diary study that requires twice-daily entries must be designed with empathy for attention and recall limitations.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Key Questions to Ask</h4>
              <ul class="insight-list">
                <li>What kind of memory will this require?</li>
                <li>Is this a reflective or real-time task?</li>
                <li>Will users be multitasking or focused?</li>
              </ul>
            </div>
            
            <p class="modal-text">Good method design isn't just about extracting data, it's about supporting users as they share it.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Method as Advocacy</h3>
            <p class="modal-intro-text">Finally, I see method selection as a subtle form of advocacy. When you choose to include marginalized users, when you design for low-bandwidth access, when you take the extra time to translate a survey into a user's first language, you're making a claim about whose experience matters.</p>
            
            <p class="modal-text">In this sense, choosing a method is never neutral. It's always political. And that's okay, so long as you're aware of it.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">In Closing</h3>
            <p class="modal-intro-text">If there's one thing I've learned, it's this: methods are not mere tools. They are commitments. They define the shape of our understanding and the quality of our empathy.</p>
            
            <p class="modal-text">Select them not just with efficiency, but with care.</p>
          </section>
          
          <div class="modal-footer">
            <a href="/writings" class="btn btn-secondary">Back to Writings</a>
          </div>
        </div>
      `
    },
    {
      id: 'psychology-design',
      title: 'The Psychology Behind Creating a User-Centered Design',
      tags: ['Cognitive Psychology', 'UX Design'],
      description: [
        'User-centered design is often discussed in terms of empathy, usability, and accessibility. These are all essential, but in practice, many "user-centered" experiences still fall short; not because they lack intention, but because they ignore something more fundamental: how the mind actually works.',
        'Cognitive psychology offers us not just a language for talking about users, but a framework for understanding their limitations, capabilities, and mental patterns.'
      ],
      content: `
        <div class="project-modal-content">
          <section class="modal-section">
            <h3 class="modal-section-title">Research Context</h3>
            <div class="modal-content-block">
              <p class="modal-text">If design is the act of shaping interactions, then psychology is the raw material we're shaping around.</p>
              <p class="modal-text">As a researcher working at the intersection of HCI and cognition, I've come to see user-centered design not as a vague value system, but as a psychologically grounded commitment: designing systems that align with how people perceive, decide, remember, and feel.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Working Memory and the Myth of the "Rational User"</h3>
            <p class="modal-intro-text">The human brain is extraordinarily powerful. But it's not built for rational, consistent calculation. It's built for speed, survival, and social interaction. This is why working memory, the mental scratchpad we use for holding temporary information, is incredibly limited. Most people can juggle about 4–7 items at once before performance drops dramatically.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Design Implications</h4>
              <p class="modal-text">When a user interface requires someone to remember a sequence of steps, track multiple conditions, or compare unfamiliar options side by side, it's effectively demanding more than our minds are built to handle. This is often when frustration sets in.</p>
              <p class="modal-text">Good design works around this. It externalizes memory. It shows you where you are in a process. It provides clear visual hierarchies and groupings. It uses progressive disclosure instead of overwhelming you with options. In short, it offloads mental effort.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Recognition Over Recall</h3>
            <p class="modal-intro-text">In cognitive science, there's a principle that recognition is easier than recall. That's why multiple-choice questions are generally easier than open-ended ones. We're much better at recognizing a familiar item than pulling it from memory unassisted.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Interface Design Applications</h4>
              <p class="modal-text">This principle has direct implications for interface design. Systems should present options rather than require users to remember commands. Menus, autocomplete fields, visual cues; all of these support recognition. Compare a command-line interface with a graphical one. The CLI might be more powerful, but the GUI lowers the memory barrier.</p>
              <p class="modal-text">Designers sometimes underestimate the power of a good label or icon. But when chosen well, these cues anchor cognition. They act as scaffolds for user decision-making.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">The Principle of Least Effort</h3>
            <p class="modal-intro-text">Herbert Simon, in discussing human decision-making, wrote that people are "satisficers", not optimizers. We don't search exhaustively for the best possible option; we go with what seems good enough. This is partly because of attention limits, but also because of cognitive economy: we conserve effort whenever possible.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Real-World Applications</h4>
              <p class="modal-text">This shows up in everything from form design to information architecture. Users will often pick the most visible or easiest-to-understand option, even if it's suboptimal. If a signup button is buried beneath five paragraphs of legalese, most people won't read. They'll either click blindly or leave.</p>
              <p class="modal-text">User-centered design means aligning your system's structure with how people actually navigate options, not how you wish they would.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Mental Models: The Hidden Maps Users Carry</h3>
            <p class="modal-intro-text">A mental model is a user's internal understanding of how a system works. These models are built from prior experiences, social learning, and interface cues. When a mental model matches the actual system behavior, things feel intuitive. When they don't, users make errors or worse, they blame themselves.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">The Save Icon Example</h4>
              <p class="modal-text">One example I often use is the "Save" function in desktop applications. For years, this icon was represented by a floppy disk: an obsolete technology that few younger users have actually used. Yet it remained effective because it became a <em>conventional</em> symbol. Mental models aren't always literal; they're learned associations.</p>
            </div>
            
            <p class="modal-text">This is why interface consistency matters. If the "back" button sometimes navigates and sometimes closes the app, users will form a confused or fragmented model. That cognitive dissonance creates friction.</p>
            
            <p class="modal-text">In research, I often ask users to describe what they think is happening behind the scenes when they take an action. Their answers aren't always correct, but they reveal how they interpret system logic and that's what design has to respond to.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Emotion, Confidence, and the Experience of Use</h3>
            <p class="modal-intro-text">Cognition isn't cold calculation. It's deeply tied to emotion. A user who feels confused or overwhelmed may become risk-averse or abandon a task. A user who feels confident is more likely to explore.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Confidence vs Delight</h4>
              <p class="modal-text">Designers talk a lot about delight, but from a psychological standpoint, confidence is often more critical. Do users feel in control? Do they feel competent? Is the interface reinforcing their sense of progress?</p>
              <p class="modal-text">Progress indicators, undo functionality, feedback animations; all of these contribute to emotional regulation during interaction. Even something as simple a "Success" message can ease cognitive dissonance.</p>
            </div>
            
            <p class="modal-text">One of my favorite findings from affective computing is that users who feel slightly uncertain are actually more attentive. That uncertainty is a productive zone, so long as the system guides them through it. Too much friction, and they shut down. But the right level of challenge engages both mind and emotion.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Design as Cognitive Collaboration</h3>
            <p class="modal-intro-text">Ultimately, I think of user-centered design as a kind of cognitive collaboration. We're not designing for users so much as designing <em>with</em> their minds in mind. That means understanding not only what they need, but how they think.</p>
            
            <p class="modal-text">It also means we must stop blaming users for "not reading", "clicking the wrong thing", or "missing the obvious". If that happens consistently, the problem isn't their cognition, it's our design.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Final Thoughts</h3>
            <p class="modal-intro-text">Cognitive psychology doesn't replace empathy, it deepens it. It gives us the language and models to anticipate user struggle before it becomes failure. It helps us design systems that feel intuitive not because they're minimal, but because they match how people actually process the world.</p>
            
            <p class="modal-text">If we want to call ourselves user-centered, we must first be mind-aware. Because behind every click, scroll, or tap is a brain doing its best.</p>
          </section>
          
          <div class="modal-footer">
            <a href="/writings" class="btn btn-secondary">Back to Writings</a>
          </div>
        </div>
      `
    },
    {
      id: 'mixed-methods',
      title: 'Mixed Methods in UX Research: Combining Qualitative and Quantitative Approaches',
      tags: ['Mixed Methods', 'Research Design'],
      description: [
        'In UX research, there\'s a persistent temptation to ask: "Which method is better - qualitative or quantitative?" But I\'ve come to believe this is the wrong question. Better, perhaps, is to ask: "What dimensions of experience are we missing when we rely on only one?"',
        'As a PhD candidate conducting fieldwork and system evaluation, I frequently move between methods. In practice, I\'ve found that real understanding rarely emerges from one type of data alone.'
      ],
      content: `
        <div class="project-modal-content">
          <section class="modal-section">
            <h3 class="modal-section-title">Research Context</h3>
            <div class="modal-content-block">
              <p class="modal-text">Quantitative data can tell you that something is happening; qualitative data often tells you why. Used together, they form a picture that's not only accurate but human.</p>
              <p class="modal-text">This is the promise of mixed methods: not methodological compromise, but epistemological strength.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Why Mixed Methods?</h3>
            <p class="modal-intro-text">Mixed methods research is not just about being thorough. It's about responding to the layered nature of user experience. People act, feel, reflect, and adapt; and those processes unfold across different kinds of evidence.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Real-World Example</h4>
              <p class="modal-text">Say you're redesigning a search feature. A usability test might show a 60% task success rate. That's helpful. But pairing that with interview data might reveal that users don't understand the terminology used in filters or that they're unsure what kind of results they should expect.</p>
              <p class="modal-text">The quantitative result gives you a signal. The qualitative result gives you a story. When both align, you have validation. When they diverge, you have an opportunity for deeper insight.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Designing a Mixed Methods Study</h3>
            <p class="modal-intro-text">Good mixed methods research doesn't just mash together tools. It requires careful sequencing, sampling, and synthesis.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Basic Models for Structuring Mixed Methods</h4>
              <ul class="insight-list">
                <li><strong>Sequential Exploratory:</strong> Start with qualitative research (e.g., interviews), then use those findings to design a quantitative instrument (e.g., survey).</li>
                <li><strong>Sequential Explanatory:</strong> Begin with quantitative data to identify trends, then follow up with qualitative research to explain anomalies or user reasoning.</li>
                <li><strong>Concurrent Triangulation:</strong> Conduct both types simultaneously and compare findings for convergence or contradiction.</li>
              </ul>
            </div>
            
            <p class="modal-text">Each approach has tradeoffs. Exploratory studies are rich but time-intensive. Triangulation can highlight inconsistencies but requires careful analytical integration. What matters is not the format, it's the clarity of purpose. Why are you combining methods? What will each add?</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Integrating Findings: More Than Just Juxtaposition</h3>
            <p class="modal-intro-text">One of the most common pitfalls I see is using mixed methods in parallel without actually integrating the findings. A team runs a usability test, sends out a survey, and presents both results in separate decks. That's not synthesis, it's colocation.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Key Integration Questions</h4>
              <ul class="insight-list">
                <li>Where do the two data types reinforce each other?</li>
                <li>Where do they contradict, and why?</li>
                <li>How does each data type shift our interpretation of the other?</li>
              </ul>
            </div>
            
            <p class="modal-text">For instance, imagine a pattern where users complete a task successfully (as measured by logs) but rate the experience as frustrating. Without qualitative input, this dissonance might be dismissed. But user narratives might reveal that although the interface "worked," it felt unintuitive or required workaround strategies.</p>
            
            <p class="modal-text">This kind of layered analysis helps product teams avoid shallow conclusions and surface root causes, not just symptoms.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Choosing What to Measure and Why</h3>
            <p class="modal-intro-text">The strength of mixed methods depends heavily on what you choose to measure; and how well those metrics align with user behaviors and mental models.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Cognitive Research Insights</h4>
              <p class="modal-text">A key insight from cognitive research is that people don't always report their own experiences accurately. We rationalize, forget, or misunderstand our actions. That's why pairing observed behavior with self-report is so powerful. You're comparing what users <em>say</em> they do with what they <em>actually</em> do.</p>
            </div>
            
            <p class="modal-text">In one project, I noticed a significant drop-off at a critical conversion step. Behavioral data suggested confusion, but interviews revealed something more emotional: users didn't trust the company enough to commit. The issue wasn't UI design; it was perceived credibility.</p>
            
            <p class="modal-text">Had I relied on numbers alone, I would've redesigned the interface. With mixed methods, we improved the brand voice and user onboarding flow instead and retention increased.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Mixed Methods and Stakeholder Communication</h3>
            <p class="modal-intro-text">In product teams, mixed methods research is also an asset for communication. Different stakeholders respond to different forms of evidence. A CFO might value statistical trends; a product designer might need a user quote to spark empathy.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Structured Presentation Approach</h4>
              <p class="modal-text">When I present mixed methods findings, I structure them in layers:</p>
              <ol class="insight-list">
                <li><strong>Top-line takeaways</strong> (what we learned)</li>
                <li><strong>Supporting evidence</strong> (a metric and a story)</li>
                <li><strong>Implications</strong> (what we do next)</li>
              </ol>
            </div>
            
            <p class="modal-text">This not only strengthens trust in the research but models rigorous, human-centered thinking. It demonstrates that insight isn't just about data, it's about meaning.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Navigating Tensions Between Methods</h3>
            <p class="modal-intro-text">Of course, combining methods isn't always smooth. Quantitative tools often require scale; qualitative ones require depth. They run on different timelines and often pull in opposite directions: one toward generalization, the other toward particularity.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Managing Methodological Tensions</h4>
              <p class="modal-text">This tension is productive, but only if acknowledged. It forces you to confront the complexity of human behavior. It invites humility: your job isn't to simplify, it's to understand.</p>
              <p class="modal-text">I've found that the best way to manage this tension is through alignment. Ensure that your methods are working on the same problem, with the same user group, during the same timeframe. This coherence makes integration possible and valuable.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Final Thoughts</h3>
            <p class="modal-intro-text">Mixed methods UX research is not just about using multiple tools. It's about cultivating a mindset; one that values pluralism, context, and the layered nature of experience.</p>
            
            <p class="modal-text">It reminds us that users are not data points, nor anecdotes. They are people; multifaceted, inconsistent, adaptive; and understanding them requires more than one lens.</p>
            
            <p class="modal-text">In a world of increasing complexity, mixed methods research offers a way to see more clearly; not just what's happening, but what matters.</p>
          </section>
          
          <div class="modal-footer">
            <a href="/writings" class="btn btn-secondary">Back to Writings</a>
          </div>
        </div>
      `
    },
    {
      id: 'data-analysis',
      title: 'The Role of Data Analysis in Modern UX Research',
      tags: ['Data Analysis', 'UX Research'],
      description: [
        'UX research is changing. As digital products generate more real-time behavioral data, researchers are increasingly expected to analyze, interpret, and communicate complex datasets. Metrics are no longer the exclusive domain of data scientists; they are central to the daily practice of UX.',
        'Data analysis can elevate UX research. It can reveal hidden patterns, validate design decisions, and highlight pain points invisible to qualitative methods alone.'
      ],
      content: `
        <div class="project-modal-content">
          <section class="modal-section">
            <h3 class="modal-section-title">Introduction</h3>
            <p class="modal-text">But without care, data can also be misleading. It can flatten the user's experience into numbers and sever the human from the behavior.</p>
            
            <p class="modal-text">In this article, I want to reflect on how I've learned to navigate data analysis as part of my UX research practice — not just technically, but philosophically.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Why Data Analysis Matters in UX</h3>
            <p class="modal-text">The interface between humans and technology generates an enormous volume of traceable behavior: clicks, taps, scrolls, exits, searches, time on task, paths abandoned, and features ignored. Within this noise is a signal: sometimes several.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Core UX Questions</h4>
              <p class="modal-text">Data analysis helps us answer core UX questions:</p>
              <ul class="insight-list">
                <li>What behaviors are users performing most often?</li>
                <li>Where are users hesitating or dropping off?</li>
                <li>What actions correlate with successful task completion?</li>
                <li>How does behavior differ across devices, segments, or time?</li>
              </ul>
            </div>
            
            <p class="modal-text">These are powerful questions, and they often lead to decisions that affect thousands or millions of users. That's why it's critical that UX researchers engage not just with the output of analytics, but with its assumptions.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">What Counts as "Data"?</h3>
            <p class="modal-text">When people say "data" in tech environments, they often mean quantitative metrics; numbers collected from logs or events. But in UX, data is broader.</p>
            
            <p class="modal-text">A researcher's field notes are data. So are interview transcripts, open-ended survey responses, screenshots of user flows, emotional expressions captured during usability tests. These are data points too rich, narrative, contextual.</p>
            
            <p class="modal-text">What analysis allows us to do is <strong>move across abstraction levels</strong>. We might go from a single user quote to a pattern in 100 responses. From an observed hesitation on a checkout screen to a 32% abandonment rate in funnel analytics. The analyst's job is to connect these layers responsibly.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Common Analytical Approaches in UX</h3>
            <p class="modal-text">In my own work, I use a range of data analysis methods depending on the question, the scope, and the data type.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Behavioral and Usage Data</h4>
              <p class="modal-text">For behavioral and usage data, I often rely on:</p>
              <ul class="insight-list">
                <li><strong>Descriptive statistics</strong>: means, medians, standard deviations. These provide a sense of spread and central tendency.</li>
                <li><strong>Funnels and pathing analysis</strong>: where users start, where they drop, and what diverging flows emerge.</li>
                <li><strong>Segmentation</strong>: comparing behavior across different user types, devices, or time periods.</li>
                <li><strong>Retention curves and cohort analysis</strong>: useful for understanding long-term engagement.</li>
              </ul>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">Qualitative Data Analysis</h4>
              <p class="modal-text">For qualitative data, my analysis involves:</p>
              <ul class="insight-list">
                <li><strong>Thematic coding</strong>: identifying recurring ideas, emotions, or barriers.</li>
                <li><strong>Affinity mapping</strong>: grouping similar responses to uncover structures.</li>
                <li><strong>Sentiment analysis</strong>: for open-ended survey fields or transcripts.</li>
              </ul>
            </div>
            
            <p class="modal-text">Increasingly, I find myself working at the boundary, where numbers and narratives intersect. A usage pattern becomes meaningful when paired with a quote. A high Net Promoter Score (NPS) becomes more useful when we understand <em>why</em> users scored it that way.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Data Literacy Is a UX Skill</h3>
            <p class="modal-text">Being data-literate doesn't mean you need to run regressions or build dashboards from scratch. But it does mean you can:</p>
            <ul class="insight-list">
              <li>Ask the right questions of your data</li>
              <li>Understand common pitfalls (like survivorship bias or Simpson's paradox)</li>
              <li>Translate results into design-relevant insights</li>
              <li>Challenge misleading interpretations</li>
            </ul>
            
            <p class="modal-text">For example, I once worked with a product team excited about an increase in feature engagement. The data showed more users clicking into a newly released tool. But after examining time-on-task and follow-up actions, I noticed that most users exited within five seconds and never returned. The spike was driven by curiosity, not utility.</p>
            
            <p class="modal-text">Had we stopped at surface-level analysis, we would have misread failure as success.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Data Without Context Is Dangerous</h3>
            <p class="modal-text">One of the lessons I've had to learn repeatedly is this: <strong>behavioral data tells you what, not why</strong>. Without qualitative context, it's easy to make assumptions that sound plausible but lack grounding.</p>
            
            <p class="modal-text">If users aren't finishing a form, it could be due to confusion. Or mistrust. Or a technical bug. Or a misplaced input field.</p>
            
            <p class="modal-text">This is why I rarely treat data analysis as standalone. I always try to pair it with user narratives, even if only a small sample. A single quote, paired with a trend line, often carries more persuasive power than either alone.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Ethical Considerations in UX Data</h3>
            <p class="modal-text">As UX researchers, we are stewards of user information. That means we must think carefully about <strong>how</strong> we analyze data and what we choose to measure.</p>
            
            <p class="modal-text">Tracking user behavior without consent is unethical. Over-collecting personal data, even if it's "anonymized", raises questions of privacy and agency. Designing metrics that serve company OKRs but not user needs distorts both research and ethics.</p>
            
            <p class="modal-text">I believe good UX research asks: are we using this data to serve users — or just to manage them?</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Data Analysis as Interpretation, Not Just Computation</h3>
            <p class="modal-text">At its core, data analysis in UX is not about math. It's about meaning.</p>
            
            <p class="modal-text">When I look at a spike in helpdesk tickets, or a drop in activation rate, I'm not just crunching numbers. I'm telling a story about frustration, confusion, hesitation. These numbers are fingerprints of human experience.</p>
            
            <p class="modal-text">The best UX researchers don't treat analysis as a separate phase. They integrate it into every step: designing studies that yield interpretable results, asking questions shaped by context, and presenting data in a way that's both rigorous and empathetic.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Final Thoughts</h3>
            <p class="modal-text">Modern UX research is inseparable from data. But it's up to us to ensure that data doesn't eclipse users themselves.</p>
            
            <p class="modal-text">We must treat analysis not as a path to certainty, but as a way to listen at scale: to hear, in aggregate, the murmur of human experience as it moves through our designs.</p>
            
            <p class="modal-text">Data is never just a dashboard. It's a mirror. And how we read it determines what kind of designers and researchers we become.</p>
          </section>
          
          <div class="modal-footer">
            <a href="/writings" class="btn btn-secondary">Back to Writings</a>
          </div>
        </div>
      `
    },
    {
      id: 'ethical-considerations',
      title: 'Ethical Considerations in UX Research',
      tags: ['Research Ethics', 'Ethics'],
      description: [
        'Ethics in UX research is not just about institutional review boards or legal compliance. It\'s about the impact of our work: how we gather information, who we include or exclude, and how our findings affect real people.',
        'As a researcher, I\'ve come to see ethics not as a static code but as a set of tensions to be navigated between learning and respecting, between curiosity and care, between insight and harm.'
      ],
      content: `
        <div class="project-modal-content">
          <section class="modal-section">
            <h3 class="modal-section-title">Introduction</h3>
            <p class="modal-text">Many of the most consequential ethical decisions don't happen in formal reviews. They happen in planning meetings, recruitment emails, usability sessions, data exports, and stakeholder debriefs.</p>
            
            <p class="modal-text">This article explores the ethical questions I return to most in my work, not just to check boxes, but to guide the kind of researcher I want to be.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Informed Consent Is a Process, Not a Checkbox</h3>
            <p class="modal-text">The first principle we're taught is informed consent. But in practice, consent often becomes procedural; a form to sign or a pop-up to click. Real consent, however, is <em>relational</em>. It's about ensuring that participants understand what they're sharing, what it will be used for, and how it may affect them.</p>
            
            <p class="modal-text">In corporate settings, I've often had to explain to stakeholders why "implicit consent" from product use isn't enough. Just because a user clicked "I agree" doesn't mean they knew their behavior would be analyzed in a research report.</p>
            
            <p class="modal-text">In my work, I try to slow this moment down. I offer plain-language explanations. I let participants ask questions. I clarify that they can skip questions or withdraw entirely. And I remind teams that ethical research isn't just about access: it's about trust.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Whose Voices Are We Listening To?</h3>
            <p class="modal-text">Representation is another ethical concern that goes beyond recruitment quotas. It's about who gets to shape the understanding of a problem.</p>
            
            <p class="modal-text">UX research often centers what's easy to study: high-frequency users, English speakers, urban populations with reliable Wi-Fi. But that leaves out edge-cases, those using assistive technologies, those with limited digital literacy, or those who've been excluded from "default" user profiles.</p>
            
            <p class="modal-text">Every time we exclude a group for convenience, we re-inscribe a digital status quo that privileges some and marginalizes others.</p>
            
            <p class="modal-text">Ethical research means going the extra mile to include harder-to-reach users; not just for equity, but for better design. Often, the edge cases show us what's broken or fragile in the system for everyone else.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">The Power Dynamics in Asking Questions</h3>
            <p class="modal-text">There's an inherent power dynamic in research: we ask, they answer. Even in user-friendly interviews, participants often want to please. They perform usefulness. They mirror back what they think we want to hear.</p>
            
            <p class="modal-text">That's why I try to remain aware of how I frame questions, how I respond to uncertainty, and how much I talk. Silence can be uncomfortable but it often allows the participant to shape the narrative.</p>
            
            <p class="modal-text">I've also learned to pay attention to how I introduce myself. Am I emphasizing expertise in a way that makes the participant feel smaller? Or am I inviting them to co-create meaning?</p>
            
            <p class="modal-text">Ethical research is dialogic. It's about listening as an act of respect.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Data Stewardship: Beyond Deletion Policies</h3>
            <p class="modal-text">We often talk about ethical data use in terms of deletion, storage, and encryption. These are essential. But equally important is the question of <em>interpretation</em>.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Key Questions</h4>
              <ul class="insight-list">
                <li>Are we interpreting this data in a way that reflects users' intent?</li>
                <li>Are we using it to improve their experience, or merely to optimize engagement?</li>
                <li>Are we anonymizing, but still extracting in ways that feel exploitative?</li>
              </ul>
            </div>
            
            <p class="modal-text">In one project, I noticed that repeated user session recordings were being reviewed without context just for "general inspiration". It made me uncomfortable. The users hadn't consented to that level of scrutiny. So I advocated for limiting playback to sessions tied to active usability questions, and for deleting recordings within a set window.</p>
            
            <p class="modal-text">We can't just protect data from breaches; we have to protect it from misuse, even internally.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Ethical Stakeholder Management</h3>
            <p class="modal-text">UX researchers often serve as intermediaries between users and powerful institutions. This can put us in difficult positions.</p>
            
            <p class="modal-text">We may find ourselves asked to design studies that feel extractive, to downplay uncomfortable findings, or to "massage" quotes to support a feature roadmap. Sometimes we're asked to simplify nuance in the name of clarity.</p>
            
            <p class="modal-text">I believe ethical practice requires some degree of moral courage. It means pushing back, gently but firmly, when research is used to justify decisions that may harm or mislead. It means refusing to reduce users to KPIs when the real issue is their wellbeing.</p>
            
            <p class="modal-text">Sometimes, the most ethical thing a researcher can do is to say: "We need to pause and reframe the question."</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Ethics in Method, Not Just Topic</h3>
            <p class="modal-text">A common misconception is that ethical risk only arises in sensitive topics — mental health, financial vulnerability, trauma. But even "neutral" topics can carry ethical implications.</p>
            
            <p class="modal-text">For example, in a study about navigation behavior, I noticed a participant becoming visibly anxious. She didn't want to seem "bad at tech." She was performing competence. That moment reminded me that <em>being observed</em> is itself a stressor. It affects people emotionally, even in mundane contexts.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Ethical Research Design Considerations</h4>
              <ul class="insight-list">
                <li>The time demands placed on participants</li>
                <li>The emotional toll of certain tasks</li>
                <li>The tone of feedback mechanisms</li>
              </ul>
            </div>
            
            <p class="modal-text">We don't just study people; we affect them. That's a responsibility we should never take lightly.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Final Reflections</h3>
            <p class="modal-text">Ethics in UX research isn't just about doing no harm. It's about practicing care toward participants, their data, and the truths they entrust us with.</p>
            
            <p class="modal-text">I don't always get it right. None of us do. But I try to stay in conversation with myself, with colleagues, and with the people I study. Ethics, after all, is not a fixed destination. It's a practice of ongoing attention.</p>
            
            <p class="modal-text">And if we truly believe in user-centered design, then ethics isn't separate from research: it <em>is</em> the research.</p>
          </section>
          
          <div class="modal-footer">
            <a href="/writings" class="btn btn-secondary">Back to Writings</a>
          </div>
        </div>
      `
    },
    {
      id: 'research-to-action',
      title: 'From Research to Action: Translating UX Insights into Design Decisions',
      tags: ['UX Strategy', 'Design Process'],
      description: [
        'Every UX researcher knows the feeling: you\'ve just wrapped a well-run study. The findings are thoughtful, the insights rich, the implications clear. But then — nothing. No changes are made. The design direction stays the same. The report is skimmed, perhaps nodded at, then filed away.',
        'The truth is, conducting great research is only half the job. The other half, and arguably the harder part, is making that research usable: understood by stakeholders, embraced by designers, acted on by teams.'
      ],
      content: `
        <div class="project-modal-content">
          <section class="modal-section">
            <h3 class="modal-section-title">Introduction</h3>
            <p class="modal-text">Translating research into design decisions is not an output problem; it's a communication, trust, and timing problem.</p>
            
            <p class="modal-text">This article explores how I've come to approach this translation work: not as an afterthought, but as a core part of my research practice.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Insights Are Only Useful if They're Heard</h3>
            <p class="modal-text">An insight is a bridge; it connects what users experience to what teams can act on. But too often, insights are locked in long documents, written in language that's either too academic or too vague. Teams don't ignore research because they don't care. They ignore it because it doesn't speak their language or solve their problem <em>now</em>.</p>
            
            <p class="modal-text">Early in my doctoral research, I was guilty of over-documentation. I would spend days coding transcripts, refining themes, writing elegant findings sections. Then I'd present them to a product team already knee-deep in sprints. I learned, quickly, that relevance beats thoroughness. Timeliness beats polish.</p>
            
            <p class="modal-text">Now, I build communication into the research process from the start. I ask: Who needs to hear this? When will they be most receptive? What format will move them?</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Design Doesn't Speak Research. Translate Accordingly.</h3>
            <p class="modal-text">UX researchers often speak in frameworks: mental models, task flows, journey stages, friction points. Designers think in screens, states, and interactions. Engineers think in logic and structure. Product managers think in metrics and OKRs.</p>
            
            <p class="modal-text">This isn't a hierarchy. It's a translation challenge.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Pairing Research with Design Artifacts</h4>
              <p class="modal-text">I've found that pairing research with design artifacts accelerates adoption. For instance:</p>
              <ul class="insight-list">
                <li>Instead of just reporting a pain point, I'll mock up how it could look in the UI.</li>
                <li>Instead of listing findings, I'll link them directly to screens in the design file.</li>
                <li>Instead of creating standalone personas, I'll work with the designer to embed behavior insights directly into component documentation.</li>
              </ul>
            </div>
            
            <p class="modal-text">The goal is not to "dumb down" the research. It's to shape it so it enters the workflow <strong>without friction</strong>.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Timing Matters as Much as Insight</h3>
            <p class="modal-text">Even the best research can be rendered moot by bad timing. If findings arrive after design decisions are made or too early to feel relevant, they risk irrelevance.</p>
            
            <p class="modal-text">That's why I aim to insert research <strong>into the rhythm of design</strong>, not on the edges. I join planning meetings, offer just-in-time insights, and shape research plans around sprint cycles. I let go of perfection in favor of presence.</p>
            
            <p class="modal-text">Sometimes, I'll run small, scrappy tests just to get directional input before a big design kickoff. Other times, I'll revisit older research and reframe it in terms that match current priorities.</p>
            
            <p class="modal-text">UX research is not a static archive. It's a living dialogue with the present moment.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Framing: From Findings to Meaning</h3>
            <p class="modal-text">There's a subtle but powerful difference between a "finding" and an "insight."</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Finding vs. Insight</h4>
              <ul class="insight-list">
                <li>A finding is: <em>Users had trouble locating the filter menu.</em></li>
                <li>An insight is: <em>Users expect filters to appear only after they begin searching. The current placement breaks that mental model.</em></li>
              </ul>
            </div>
            
            <p class="modal-text">Findings describe what happened. Insights explain <em>why it matters</em>. This difference is critical when trying to influence design decisions.</p>
            
            <p class="modal-text">I use a simple structure to move from raw observation to design implication:</p>
            <ol class="insight-list">
              <li><strong>What did we observe?</strong> (e.g., 5 of 6 users skipped over a key CTA)</li>
              <li><strong>What does this suggest?</strong> (e.g., the CTA's placement contradicts scanning patterns)</li>
              <li><strong>What should we consider?</strong> (e.g., relocating the CTA to the primary visual flow)</li>
            </ol>
            
            <p class="modal-text">This structure helps stakeholders not just <em>know</em> what we found but <em>care</em>.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">The Researcher as Facilitator, Not Just Analyst</h3>
            <p class="modal-text">Over time, I've come to see the UX researcher not just as a knowledge generator, but as a <strong>facilitator of shared understanding</strong>.</p>
            
            <div class="insight-card">
              <h4 class="insight-title">Facilitation Practices</h4>
              <p class="modal-text">This might mean:</p>
              <ul class="insight-list">
                <li>Running workshops where stakeholders interact directly with raw data</li>
                <li>Creating "research walls" or digital spaces where team members can see quotes, screenshots, and journey maps evolve</li>
                <li>Facilitating sketch sessions informed by real user stories</li>
              </ul>
            </div>
            
            <p class="modal-text">These practices don't just transfer insights, they build empathy. They collapse the gap between "users" and "us".</p>
            
            <p class="modal-text">And they signal that research isn't a report: it's a <strong>practice of collective meaning-making</strong>.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Dealing with Resistance</h3>
            <p class="modal-text">Sometimes, even clear, well-timed, actionable insights are dismissed. Teams may already be overcommitted. Stakeholders may be defensive. The findings may contradict assumptions that are politically risky to challenge.</p>
            
            <p class="modal-text">In these moments, I remind myself: <strong>research doesn't need to win the argument: it needs to keep the door open.</strong></p>
            
            <p class="modal-text">I document findings clearly, archive quotes and recordings, and make it easy to revisit the data later. Often, the same stakeholder who rejected the insight will return weeks later when the problem persists.</p>
            
            <p class="modal-text">Truth has a way of resurfacing, especially when it's grounded in real user experience.</p>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Final Thoughts</h3>
            <p class="modal-text">If UX research is about understanding, then research translation is about <strong>making that understanding actionable</strong>. It's about advocacy, timing, and thoughtful storytelling. It's where research earns its relevance.</p>
            
            <p class="modal-text">The most powerful research insights don't just describe users - they <strong>move teams</strong>. They change roadmaps. They alter flows. They bring a bit more clarity, humanity, and nuance into how we design the systems people live with every day.</p>

            <p class="modal-text">As a researcher, I've come to believe that insight alone is not enough. Insight must <em>travel</em> and our job is to build the path.</p>
          </section>
          
          <div class="modal-footer">
            <a href="/writings" class="btn btn-secondary">Back to Writings</a>
          </div>
        </div>
      `
    }
  ];

  return (
    <div className="App">
      <IdentityCard />
      {/* Hero Section with Introduction Card */}
      <section className="hero-section">
        <div className="container">
          <div className="text-center animate-fade-in-up">
            <h1 className="page-title">Writings</h1>
          </div>
          <div className="hero-card writings-hero-card animate-fade-in-up">
            <div className="hero-card-content">
              <div className="hero-card-description">
                <p>
                  I often find myself reflecting on the cognitive, emotional, and social dynamics that shape how people interact with systems. These articles are not formal papers or academic publications. They are personal reflections, extensions of my reading, experimentation, and continuous inquiry.
                </p>
                <div className="question-text">
                  <p>How do we choose methods that serve people and not just metrics?</p>
                  <p>How does cognitive psychology inform usable, inclusive design?</p>
                  <p>How can we ensure our work remains critical, ethical, and human?</p>
                </div>
                <p className="closing-text">
                  These pieces are rooted in my evolving practice as a researcher. I write not as an authority, but as a fellow inquirer: thinking aloud, and sometimes in public.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="case-studies-section">
        <div className="container">
          <div className="grid grid-2 animate-stagger">
            {articles.map((article) => (
              <div key={article.id} className="card">
                <h3 className="card-title">{article.title}</h3>
                <div className="card-tags">
                  {article.tags.map((tag) => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                </div>
                {article.description.map((paragraph, index) => (
                  <p key={index} className="card-description">
                    {paragraph}
                  </p>
                ))}
                <button 
                  className="btn-link"
                  onClick={() => handleArticleClick(article.id)}
                >
                  Read More
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal for articles */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={closeModal}
        title={articles.find(a => a.id === selectedArticle)?.title || ''}
      >
        {selectedArticle && (
          <div dangerouslySetInnerHTML={{ 
            __html: articles.find(a => a.id === selectedArticle)?.content || '' 
          }} />
        )}
      </Modal>
    </div>
  );
};

export default Writings;
